import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Banner, Page, Spinner } from '@shopify/polaris';
import { Card } from '@/components/card/card';
import { Book } from 'lucide-react';
import { HelpSidebar } from '@/modules/help/components';
import { HelpArticle } from '@/modules/help/components';
import { HelpBreadcrumbs } from '@/modules/help/components';
import { HelpFeaturedCard } from '@/modules/help/components';
import { getHelpArticle, getHelpNavigation } from '@/api/help-api';

/**
 * Help Center Page
 *
 * Main layout with fixed-width sidebar (280px) and responsive grid.
 *
 * SSR Integration:
 * Blade renders full HTML for SEO (Google sees everything).
 * Server pre-seeds data via window.__HELP_CENTER_DATA__ so React
 * skips the initial AJAX fetch — no loading spinner, instant mount.
 * Subsequent SPA navigation fetches via API as before.
 *
 * Why .hc-* classes instead of CSS modules: single source of truth in
 * resources/css/help-center.scss shared with Blade SSR.
 *
 * Location: resources/react/src/modules/help/pages/help-page.jsx
 *
 * @component
 */
export const HelpPage = () => {
  const { category, slug } = useParams();
  const navigate = useNavigate();

  // Read pre-seeded data from SSR (Blade injects into window.__HELP_CENTER_DATA__)
  // This eliminates the loading spinner on initial page load.
  const preSeeded = window.__HELP_CENTER_DATA__ || null;

  const [loading, setLoading] = useState(!preSeeded);
  const [error, setError] = useState(null);
  const [navigation, setNavigation] = useState(preSeeded?.navigation || []);
  const [article, setArticle] = useState(preSeeded?.article || null);
  const [featuredArticles, setFeaturedArticles] = useState(
    preSeeded?.featured_articles || []
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Load navigation on mount — skip if pre-seeded by SSR
  useEffect(() => {
    if (preSeeded?.navigation?.length > 0) {
      // Clear pre-seeded data after consumption to avoid stale state
      // on subsequent SPA navigations within the same session
      window.__HELP_CENTER_DATA__ = null;
      return;
    }
    loadNavigation();
  }, []);

  // Load article when route changes — skip initial if pre-seeded
  useEffect(() => {
    if (category && slug) {
      // Skip fetch if article was pre-seeded and matches current route
      if (article?.category === category && article?.slug === slug) {
        setLoading(false);
        return;
      }
      loadArticle(category, slug);
      setSidebarOpen(false); // Close sidebar on mobile after article select
    } else {
      setArticle(null);
      setLoading(false);
    }
  }, [category, slug]);

  const loadNavigation = async () => {
    try {
      const data = await getHelpNavigation();
      setNavigation(data.navigation || []);
      setFeaturedArticles(data.featured_articles || []);
    } catch (err) {
      console.error('Failed to load navigation:', err);
      setError('Unable to load help navigation');
    }
  };

  const loadArticle = async (cat, slg) => {
    setLoading(true);
    setError(null);

    try {
      const data = await getHelpArticle(cat, slg);
      setArticle(data);
    } catch (err) {
      console.error('Failed to load article:', err);
      setError(err.message || 'Unable to load article');
    } finally {
      setLoading(false);
    }
  };

  const handleArticleSelect = (cat, slg) => {
    navigate(`/help-center/${cat}/${slg}`);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Home page view (no article selected)
  const renderHomePage = () => (
    <div className="hc-home-content">
      <Card>
        <div className="hc-hero">
          <div className="hc-hero__icon">
            <Book size={48} strokeWidth={1.5} />
          </div>
          <h2 className="hc-hero__title">DemandLoop Help Center</h2>
          <p className="hc-hero__description">
            Find guides, tutorials, and answers to common questions
          </p>
        </div>
      </Card>

      {featuredArticles.length > 0 && (
        <div className="hc-featured">
          <h3 className="hc-featured__title">Featured Articles</h3>
          <div className="hc-featured__grid">
            {featuredArticles.map((item) => (
              <HelpFeaturedCard
                key={`${item.category}-${item.slug}`}
                title={item.title}
                description={item.description}
                category={item.category}
                onClick={() => handleArticleSelect(item.category, item.slug)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Article view
  const renderArticle = () => {
    if (loading) {
      return (
        <div className="hc-article-wrap">
          <Card>
            <div className="hc-loading-state">
              <Spinner size="large" />
            </div>
          </Card>
        </div>
      );
    }

    if (error) {
      return (
        <div className="hc-article-wrap">
          <Banner tone="critical" title="Error loading article">
            {error}
          </Banner>
        </div>
      );
    }

    if (!article) {
      return null;
    }

    return (
      <div className="hc-article-wrap">
        <HelpBreadcrumbs
          category={category}
          articleTitle={article.title}
          navigation={navigation}
        />
        <HelpArticle article={article} />
      </div>
    );
  };

  // Why conditional title: article pages have their own H1, showing "Help Center"
  // as a second heading adds visual noise — breadcrumbs already provide navigation context
  return (
    <Page title={article ? '' : 'Help Center'} fullWidth>
      <div className="hc-layout">
        {/* Mobile Sidebar Toggle */}
        <button
          type="button"
          className="hc-sidebar-toggle"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          {sidebarOpen ? '✕' : '☰'}
        </button>

        {/* Sidebar - Fixed width 280px */}
        <aside
          className={`hc-sidebar ${sidebarOpen ? 'hc-sidebar--open' : ''}`}
        >
          <HelpSidebar
            navigation={navigation}
            activeCategory={category}
            activeSlug={slug}
            onArticleSelect={handleArticleSelect}
          />
        </aside>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="hc-sidebar-overlay"
            onClick={toggleSidebar}
            aria-hidden="true"
          />
        )}

        {/* Main Content */}
        <main className="hc-main-content">
          {category && slug ? renderArticle() : renderHomePage()}
        </main>
      </div>
    </Page>
  );
};
